import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faCalendarCheck, faImage, faDownload, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import CommonTable from "../../../common/table/ComonTable";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";

const AmenitiesList = () => {
    const [amenities, setAmenities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });
    
    const navigate = useNavigate();

    const fetchAmenities = useCallback(async (page = pagination.pageNumber, customPageSize = null) => {
        setLoading(true);
        try {
            const pageSize = customPageSize || pagination.pageSize;
            const res = await apiServices({
                url: `/ResidentialAmenity/GetAllResidentialAmenities`,
                method: 'GET'
            });

            const dataArray = res.data?.data || res.data || [];

            if ((res.success || res.isSuccess || res.data?.IsSuccess) && Array.isArray(dataArray)) {
                let mapped = dataArray.map(a => ({
                    id: a.amenityId,
                    amenityName: a.amenityName,
                    amenityType: a.amenityTypeName || 'N/A',
                    capacity: a.capacity,
                    charges: a.charges || 0,
                    isActive: a.status || a.isActive || 'Inactive',
                    description: a.description || 'No description provided.',
                    image: a.Image || a.image || '',
                    availableDays: (a.availableDays || 'Not Specified').split(',').join(', '),
                    startTime: a.startTime || '-',
                    endTime: a.endTime || '-',
                    isBookable: a.isbookable === 1 || a.isbookable === '1' || a.isbookable === true || a.isbookable === 'true' || a.isbookable === 'Yes' ? 'Yes' : 'No'
                }));
                mapped.sort((a, b) => b.id - a.id);
                
                // Client-side search filtering
                if (searchTerm) {
                    mapped = mapped.filter(item => 
                        item.amenityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.amenityType?.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                }

                setAmenities(mapped);
                setPagination({
                    pageNumber: Number(page),
                    pageSize: Number(pageSize),
                    totalRecords: mapped.length,
                    totalPages: Math.ceil(mapped.length / pageSize) || 1
                });
            } else {
                setAmenities([]);
                setPagination(prev => ({ ...prev, totalRecords: 0 }));
            }
        } catch (error) {
            console.error("Error fetching amenities:", error);
            toast.error("Failed to fetch amenities");
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize, searchTerm]);

    useEffect(() => {
        fetchAmenities();
    }, [fetchAmenities]);

    const handleLimitChange = (newLimit) => {
        fetchAmenities(1, newLimit);
    };

    const onPageChange = (page) => {
        fetchAmenities(page);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, pageNumber: 1 }));
    };

    const handleAddNew = () => {
        navigate('/zentres/Amenities-Management/Amenities-form/add');
    };

    const handleEdit = (row) => {
        navigate(`/zentres/Amenities-Management/Amenities-form/${row.id}`);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this amenity?")) return;
        try {
            const res = await apiServices({
                url: `/ResidentialAmenity/DeleteResidentialAmenity/${id}`,
                method: 'DELETE' // Or whatever the actual method is
            });
            if (res.success) {
                toast.success("Amenity deleted successfully");
                fetchAmenities();
            } else {
                toast.error(res.data?.Message || "Failed to delete amenity");
            }
        } catch (error) {
            console.error("Error deleting amenity:", error);
            toast.error("Failed to delete amenity");
        }
    };

    const tableHeaders = [
        { label: "S. No", key: "sno", align: "center" },
        { label: "AMENITY NAME", key: "amenityName", align: "left" },
        { label: "TYPE", key: "amenityType", align: "left" },
        { label: "CAPACITY", key: "capacity", align: "center" },
        { label: "CHARGES", key: "charges", align: "center" },
        { label: "STATUS", key: "status", align: "center" },
        { label: "ACTIONS", key: "actions", align: "center" }
    ];

    const renderTableCell = (key, row, idx) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{((pagination.pageNumber - 1) * pagination.pageSize) + row.originalIndex + 1}</span>;
            case "amenityName":
                return (
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-gray-800 tracking-tight">{row.amenityName}</span>
                    </div>
                );
            case "amenityType":
                return <span className="font-medium text-xs text-[#64748B]">{row.amenityType}</span>;
            case "capacity":
                return <span className="font-bold text-xs text-gray-700">{row.capacity} Persons</span>;
            case "charges":
                return (
                    <span className={`font-bold text-xs ${!row.charges || row.charges === '0' ? 'text-green-600' : 'text-blue-600'}`}>
                        {!row.charges || row.charges === '0' ? 'Free' : `₹${row.charges}`}
                    </span>
                );
            case "status":
                let statusColor = "bg-gray-100 text-gray-600";
                if (row.isActive === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (row.isActive === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {row.isActive || 'Inactive'}
                    </span>
                );
            case "actions":
                return null;
            default:
                return row[key] || "-";
        }
    };

    const expandedRowRender = (row) => {
        return (
            <div className="p-6 bg-[#F8FAFC80] flex flex-col md:flex-row gap-6 items-start rounded-b-xl border-b border-l border-r border-gray-200">
                <div className="w-full md:w-48 h-32 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden border border-gray-200 shadow-sm">
                    {row.image ? (
                        <img src={row.image} alt={row.amenityName} className="w-full h-full object-cover" />
                    ) : (
                        <div className="flex flex-col items-center justify-center text-gray-400">
                            <FontAwesomeIcon icon={faImage} className="text-3xl mb-2 text-gray-300" />
                            <span className="text-[10px] uppercase font-bold">No Image</span>
                        </div>
                    )}
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
                    <div className="col-span-full md:col-span-2">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Description</h4>
                        <p className="text-sm text-gray-700 whitespace-normal">{row.description}</p>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Available Days</h4>
                        <p className="text-sm font-semibold text-gray-800">{row.availableDays}</p>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Timing</h4>
                        <p className="text-sm font-semibold text-gray-800">{row.startTime} - {row.endTime}</p>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Is Bookable</h4>
                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${row.isBookable === 'Yes' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                            {row.isBookable}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const handleExport = () => {
        if (!amenities || amenities.length === 0) {
            toast.info("No data to export");
            return;
        }
        // In a real scenario, use exportToExcel from utils, but for now just show toast or use basic CSV logic
        toast.success("Exporting amenities list...");
    };

    const headerAction = (
        <div className="flex items-center gap-3">
            <div className="relative w-64 hidden sm:block">
                <input
                    type="text"
                    placeholder="Search amenities..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-2 bg-[#F8FAFC] border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            </div>
            <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B] text-xs font-bold rounded-lg hover:bg-white hover:text-[#1E293B] hover:border-[#1E293B] transition-all duration-200 shadow-sm"
            >
                <FontAwesomeIcon icon={faDownload} className="text-sm" />
                <span className="hidden sm:inline">Export</span>
            </button>
        </div>
    );

    const displayData = amenities.slice((pagination.pageNumber - 1) * pagination.pageSize, pagination.pageNumber * pagination.pageSize);

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />
            
            <PageHeadingSection
                mainHeading="Amenities Management"
                subHeading="Manage residential amenities, monitor availability, and track their usage properties."
                primaryBtnText="Add Amenity"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={handleAddNew}
                seconadaryBtnText="Bookings"
                seconadaryBtnIcon={faCalendarCheck}
                SecandaryOnClick={() => navigate('/zentres/Amenities-Management/Amenities-Booking')}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    loading={loading}
                    tableHeading="Amenities List"
                    headers={tableHeaders}
                    data={displayData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row, idx) => renderTableCell(key, row, idx)}
                    headerAction={headerAction}
                    pagination={pagination}
                    onPageChange={onPageChange}
                    onLimitChange={handleLimitChange}
                    onEdit={handleEdit}
                    onDelete={(row) => handleDelete(row.id)}
                    expandedRowRender={expandedRowRender}
                />
            </div>
        </div>
    );
};

export default AmenitiesList;
